import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calculator, 
  ChevronRight, 
  ArrowRight,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  BookOpen,
  CheckCircle,
  Clock,
  Calendar,
  Ruler,
  Target,
  Info
} from 'lucide-react';

const PortanceMicropieu = () => {
  const [formData, setFormData] = useState({
    diametre: '',
    longueur: '',
    sol: '',
    type: 'grav'
  });
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [activeSection, setActiveSection] = useState('');

  // Sections for table of contents
  const sections = [
    { id: 'section-outil', title: "L'outil de calcul" },
    { id: 'section-methode', title: "Méthode et limites" },
    { id: 'section-comprendre', title: "Comprendre les micropieux" },
    { id: 'section-guides', title: "Pour aller plus loin" },
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Plages de frottement unitaire qs INDICATIVES (kPa)
  const QS_KPA = {
    argile: { grav: 40, igu: 70, irs: 95 },
    sable: { grav: 80, igu: 130, irs: 170 },
    craie: { grav: 90, igu: 150, irs: 200 },
    marne: { grav: 100, igu: 160, irs: 210 },
    rocher: { grav: 120, igu: 190, irs: 250 }
  };
  const SECURITE = 2.0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  const calculate = () => {
    const newErrors = {};
    const diam = parseFloat(formData.diametre);
    const long = parseFloat(formData.longueur);

    if (isNaN(diam) || diam <= 0) {
      newErrors.diametre = 'Entrez un diamètre valide.';
    } else if (diam > 300) {
      newErrors.diametre = "Au-delà de 300 mm, ce n'est plus un micropieu : cet outil ne s'applique pas.";
    }

    if (isNaN(long) || long <= 0) {
      newErrors.longueur = "Entrez une longueur d'ancrage valide.";
    } else if (long > 40) {
      newErrors.longueur = 'Longueur inhabituelle : vérifiez votre saisie.';
    }

    if (!formData.sol) {
      newErrors.sol = 'Sélectionnez un type de sol.';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setResult(null);
      return;
    }

    const D = diam / 1000;
    const perimetre = Math.PI * D;
    const surfLat = perimetre * long;
    const qs = QS_KPA[formData.sol][formData.type];

    const frottementRupture = surfLat * qs;
    const admissible = frottementRupture / SECURITE;

    setResult({
      frottement: Math.round(frottementRupture),
      admissible: Math.round(admissible),
      surfLat: surfLat.toFixed(2),
      qs: qs
    });

    setTimeout(() => {
      document.getElementById('result-box')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Estimateur de portance d'un micropieu",
    "description": "Outil de pré-estimation de la capacité portante d'un micropieu selon la méthode pressiométrique (Eurocode 7).",
    "url": "https://geots.consulting/calcul/portance-micropieu",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Comment calculer la portance d'un micropieu ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "La portance d'un micropieu est principalement due au frottement latéral. On calcule : Frottement = π × Diamètre × Longueur × qs, où qs est le frottement latéral unitaire dépendant du sol et du type d'injection."
        }
      },
      {
        "@type": "Question",
        "name": "Quelle est la différence entre micropieu Type II, III et IV ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Type II (gravitaire) : coulis mis en place sans pression. Type III (IGU) : injection globale sous pression. Type IV (IRS) : injection répétitive et sélective. Plus l'injection est poussée, plus le frottement est élevé."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Estimateur de portance d'un micropieu | Calcul Eurocode 7 | GEOTS</title>
        <meta name="description" content="Estimez la portance indicative d'un micropieu selon la méthode pressiométrique (Eurocode 7 / NF P 94-262). Outil pédagogique gratuit pour pré-dimensionnement." />
        <link rel="canonical" href="https://geots.consulting/calcul/portance-micropieu" />
        <meta property="og:title" content="Estimateur de portance d'un micropieu | GEOTS" />
        <meta property="og:description" content="Calculez une estimation de la capacité portante d'un micropieu. Outil gratuit basé sur l'Eurocode 7." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geots.consulting/calcul/portance-micropieu" />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/calcul" className="hover:text-[#635BFF]">Calculs</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Portance micropieu</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  OUTIL INTERACTIF
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 23 juillet 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  ~2 min d'utilisation
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Estimateur de portance
                <span className="text-[#00D4FF]"> d'un micropieu</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Pré-estimation pédagogique basée sur les principes de la méthode pressiométrique 
                (Eurocode 7 / NF P 94-262). À usage indicatif uniquement — ne remplace pas une note de calcul.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => scrollToSection('section-outil')}
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  <Calculator className="w-5 h-5" />
                  Utiliser l'outil
                </button>
                <Link 
                  to="/guides/micropieux" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Lire le guide micropieux
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Quick disclaimer in sidebar */}
                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-start gap-2 text-amber-700 text-xs">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Estimation indicative uniquement. Validation par un BET requise.</span>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              
              {/* Section 1 - L'outil */}
              <section id="section-outil" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                  L'outil de calcul
                </h2>

                {/* Calculator Card */}
                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 mb-6">
                  
                  {/* Diamètre */}
                  <div className="mb-5">
                    <label className="block font-semibold text-[#0A2540] mb-2">
                      Diamètre du forage
                      <span className="font-normal text-gray-500 text-sm ml-2">(mm) — un micropieu fait moins de 300 mm</span>
                    </label>
                    <input
                      type="number"
                      name="diametre"
                      value={formData.diametre}
                      onChange={handleChange}
                      placeholder="ex. 200"
                      min="1"
                      max="300"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all"
                    />
                    {errors.diametre && (
                      <p className="text-red-600 text-sm mt-2">{errors.diametre}</p>
                    )}
                  </div>

                  {/* Longueur */}
                  <div className="mb-5">
                    <label className="block font-semibold text-[#0A2540] mb-2">
                      Longueur d'ancrage dans le sol porteur
                      <span className="font-normal text-gray-500 text-sm ml-2">(m)</span>
                    </label>
                    <input
                      type="number"
                      name="longueur"
                      value={formData.longueur}
                      onChange={handleChange}
                      placeholder="ex. 6"
                      min="0.1"
                      step="0.1"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all"
                    />
                    {errors.longueur && (
                      <p className="text-red-600 text-sm mt-2">{errors.longueur}</p>
                    )}
                  </div>

                  {/* Type de sol */}
                  <div className="mb-5">
                    <label className="block font-semibold text-[#0A2540] mb-2">
                      Type de sol d'ancrage
                    </label>
                    <select
                      name="sol"
                      value={formData.sol}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white"
                    >
                      <option value="">— sélectionner —</option>
                      <option value="argile">Argile / limon</option>
                      <option value="sable">Sable / grave</option>
                      <option value="craie">Craie</option>
                      <option value="marne">Marne / calcaire marneux</option>
                      <option value="rocher">Rocher altéré / fragmenté</option>
                    </select>
                    {errors.sol && (
                      <p className="text-red-600 text-sm mt-2">{errors.sol}</p>
                    )}
                  </div>

                  {/* Type de scellement */}
                  <div className="mb-6">
                    <label className="block font-semibold text-[#0A2540] mb-2">
                      Type de scellement du micropieu
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white"
                    >
                      <option value="grav">Type II — scellement gravitaire (coulis par tube plongeur)</option>
                      <option value="igu">Type III — injection globale sous pression (IGU)</option>
                      <option value="irs">Type IV — injection répétitive et sélective (IRS)</option>
                    </select>
                    <p className="text-gray-500 text-sm mt-2">
                      Plus l'injection est poussée (III, IV), plus le frottement mobilisable est élevé.
                    </p>
                  </div>

                  {/* Calculate Button */}
                  <button
                    onClick={calculate}
                    className="w-full bg-[#635BFF] hover:bg-[#5248e5] text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <Calculator className="w-5 h-5" />
                    Estimer la portance
                  </button>
                </div>

                {/* Result Section */}
                {result && (
                  <div id="result-box" className="bg-white rounded-2xl shadow-sm border overflow-hidden mb-6">
                    <div className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] p-4">
                      <h3 className="font-bold text-white text-lg">Résultat de l'estimation</h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                          <div className="text-3xl font-bold text-[#0A2540]">{result.frottement}</div>
                          <div className="text-sm text-gray-500 mt-1">Frottement latéral estimé (kN)</div>
                        </div>
                        <div className="bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-xl p-4 text-center">
                          <div className="text-3xl font-bold text-[#635BFF]">{result.admissible}</div>
                          <div className="text-sm text-gray-500 mt-1">Charge admissible estimée (kN)</div>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm mb-4">
                        Surface latérale d'ancrage : <strong>{result.surfLat} m²</strong> · 
                        frottement unitaire indicatif retenu : <strong>{result.qs} kPa</strong> · 
                        coefficient de sécurité appliqué : <strong>{SECURITE}</strong>. 
                        Résistance de pointe négligée (micropieu).
                      </p>

                      {/* Warning */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800">
                            <strong className="text-amber-700">⚠️ Estimation indicative — ne remplace pas une note de calcul.</strong>
                            <p className="mt-2">
                              Ce résultat est un ordre de grandeur pédagogique. Il repose sur des plages de valeurs 
                              moyennes et non sur les abaques exacts de la norme NF P 94-262. Il ne tient pas compte 
                              de votre modèle géotechnique réel, des couches traversées, ni de l'ensemble des 
                              vérifications de l'Eurocode 7.
                            </p>
                            <p className="mt-2">
                              <strong>Tout dimensionnement définitif doit être réalisé et validé par un bureau 
                              d'études géotechniques qualifié.</strong>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* CTA */}
                      <Link
                        to="/materiel/demande"
                        className="block mt-4 bg-[#0A2540] hover:bg-[#1a3a5c] text-white text-center font-semibold py-4 px-6 rounded-xl transition-colors"
                      >
                        Faire valider mon projet par un bureau d'études →
                      </Link>
                    </div>
                  </div>
                )}
              </section>

              {/* Section 2 - Méthode et limites */}
              <section id="section-methode" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                  Méthode et limites de l'outil
                </h2>

                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 space-y-6">
                  <div>
                    <h3 className="font-bold text-[#0A2540] mb-3 flex items-center gap-2">
                      <Info className="w-5 h-5 text-[#635BFF]" />
                      Méthode de calcul
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Pour un micropieu, la capacité provient <strong>essentiellement du frottement latéral</strong> 
                      (la résistance de pointe est négligée, car la section est trop faible). On estime :
                    </p>
                    <div className="bg-[#0A2540] text-white px-4 py-3 rounded-lg font-mono text-center mb-4">
                      Frottement = π × D × L × qs
                    </div>
                    <p className="text-gray-600">
                      où <strong>D</strong> = diamètre, <strong>L</strong> = longueur d'ancrage, et <strong>qs</strong> = frottement latéral unitaire.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#0A2540] mb-3">Valeur de qs</h3>
                    <p className="text-gray-600">
                      La valeur <code className="bg-gray-100 px-2 py-0.5 rounded text-sm">qs</code> est ici approchée par une <strong>plage moyenne</strong> selon 
                      le sol et le type d'injection. Dans la réalité normative, qs se lit dans les abaques de la 
                      NF P 94-262 à partir de la pression limite pressiométrique <code className="bg-gray-100 px-2 py-0.5 rounded text-sm">pl*</code> mesurée 
                      sur site, couche par couche.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-[#0A2540] mb-3">Coefficient de sécurité</h3>
                    <p className="text-gray-600">
                      La charge de rupture estimée est divisée par un coefficient global (≈ 2,0) pour donner une 
                      <strong> charge admissible</strong> prudente. L'Eurocode 7 utilise en réalité des coefficients 
                      partiels distincts (ELU/ELS) plus fins.
                    </p>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <h3 className="font-bold text-red-700 mb-2">Ce que cet outil ne fait PAS</h3>
                    <ul className="text-red-700 text-sm space-y-1">
                      <li>• N'intègre pas le modèle de sol multicouche</li>
                      <li>• Ne lit pas les vrais abaques normatifs</li>
                      <li>• N'applique pas les vérifications ELS de tassement</li>
                      <li>• Ne remplace en aucun cas un logiciel de calcul professionnel ou un bureau d'études</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 3 - Comprendre */}
              <section id="section-comprendre" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                  Comprendre les micropieux
                </h2>

                <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 space-y-6">
                  <p className="text-gray-600">
                    Un <strong>micropieu</strong> est un élément de fondation profonde de petit diamètre 
                    (généralement inférieur à 300 mm) qui transfère les charges de l'ouvrage vers les 
                    couches de sol porteur en profondeur. Contrairement aux pieux classiques, sa capacité 
                    portante provient <strong>quasi-exclusivement du frottement latéral</strong> entre le 
                    coulis de scellement et le sol environnant.
                  </p>

                  <div>
                    <h3 className="font-bold text-[#0A2540] mb-4">Les types d'injection (II, III, IV)</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <strong className="text-[#0A2540]">Type II (gravitaire)</strong>
                          <p className="text-gray-600 text-sm">Le coulis est mis en place par gravité via un tube plongeur. Frottement mobilisable modéré.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <strong className="text-[#0A2540]">Type III (IGU)</strong>
                          <p className="text-gray-600 text-sm">Injection globale et unitaire sous pression. Améliore significativement le frottement.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <strong className="text-[#0A2540]">Type IV (IRS)</strong>
                          <p className="text-gray-600 text-sm">Injection répétitive et sélective. Permet d'obtenir les frottements les plus élevés.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4 - Guides connexes */}
              <section id="section-guides" className="mb-16">
                <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                  Pour aller plus loin
                </h2>

                <div className="space-y-4">
                  <Link 
                    to="/guides/micropieux"
                    className="block bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-cyan-500 p-3 rounded-xl">
                          <Ruler className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0A2540]">Guide des micropieux</p>
                          <p className="text-sm text-gray-600">Types I à IV, mise en œuvre, reprise en sous-œuvre, normes applicables</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                  
                  <Link 
                    to="/guides/dimensionnement-pieux-eurocode-7"
                    className="block bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-indigo-500 p-3 rounded-xl">
                          <BookOpen className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0A2540]">Dimensionnement des pieux selon l'Eurocode 7</p>
                          <p className="text-sm text-gray-600">Méthodes de calcul, coefficients partiels et vérifications normatives</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                </div>
              </section>

              {/* Final CTA */}
              <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white text-center">
                <Calculator className="w-12 h-12 mx-auto mb-4 opacity-80" />
                <h2 className="text-2xl font-bold mb-3">Besoin d'aller plus loin ?</h2>
                <p className="text-white/80 mb-6 max-w-xl mx-auto">
                  Cet outil donne un ordre de grandeur. Pour un dimensionnement définitif, 
                  faites appel à un bureau d'études géotechniques qualifié.
                </p>
                <Link 
                  to="/materiel/demande"
                  className="inline-flex items-center gap-2 bg-white text-[#635BFF] px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors"
                >
                  Demander une étude ou un devis
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </section>

            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default PortanceMicropieu;
